import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AuthShell from "../components/AuthShell";
import AppModal from "../components/AppModal";
import TerminosYCondiciones from "../components/TerminosYCondiciones";
import { getDeviceFingerprint } from "../utils/fingerprint";
import ColombianAddressInput from "../components/ColombianAddressInput";

export default function Register() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isArrendatarioDomain = window.location.hostname.includes("mereferencio");

  const [form, setForm] = useState({
    tipo_documento: "CC",
    no_documento: "",
    first_name: "",
    last_name: "",
    email: "",
    role: isArrendatarioDomain ? "ARRENDATARIO" : "ARRENDADOR",
    password: "",
    confirm_password: "",
    tipo_organizacion: "ARRENDADOR_INDEPENDIENTE",
    empresa_id: "",
    ciudad_id: "",
    empresa_nit: "",
    empresa_direccion: "",
    empresa_telefono: "",
    empresa_email: "",
    terminos_aceptados: false,
    direccion: "",
    barrio_zona: "",
    celular: "",
    celular2: "",
  });

  // Estados para Paso 2: Códigos de verificación
  const [verification, setVerification] = useState({
    email_code: "",
    sms_otp: "",
  });
  const [channelStatus, setChannelStatus] = useState({
    email_verificado: false,
    sms_verificado: false,
  });
  const [editingPhone, setEditingPhone] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);

  const [empresas, setEmpresas] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [modal, setModal] = useState({ isOpen: false, title: "", message: "", type: "info" });
  const [isDireccionValid, setIsDireccionValid] = useState(false);
  const [isEmpresaAddressValid, setIsEmpresaAddressValid] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || ""}/api/empresas/`)
      .then(res => res.json())
      .then(data => setEmpresas(Array.isArray(data) ? data : data.results || []))
      .catch(err => console.warn("Error fetching empresas", err));

    fetch(`${process.env.REACT_APP_API_URL || ""}/api/ciudades/`)
      .then(res => res.json())
      .then(data => setCiudades(Array.isArray(data) ? data : data.results || []))
      .catch(err => console.warn("Error fetching ciudades", err));

    // Si viene desde login requiriendo verificación
    if (location.state && location.state.step === 2) {
      setStep(2);
      if (location.state.no_documento) {
        setForm(prev => ({
          ...prev,
          no_documento: location.state.no_documento,
          email: location.state.email || prev.email,
          celular: location.state.celular || prev.celular,
        }));
      }
      setChannelStatus({
        email_verificado: !!location.state.email_verificado,
        sms_verificado: !!location.state.sms_verificado,
      });
    }
  }, [location.state]);

  const showModal = (title, message, type = "info") => {
    setModal({ isOpen: true, title, message, type });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const isSensitive = name === "email" || name === "password" || name === "confirm_password";
    setForm({ ...form, [name]: isSensitive ? value : value.toUpperCase() });
  };

  const handleVerificationChange = (e) => {
    const { name, value } = e.target;
    setVerification(prev => ({ ...prev, [name]: value.trim() }));
  };

  // PASO 1: Enviar registro de nuevo usuario
  const handleSubmitStep1 = async (e) => {
    e.preventDefault();

    if (!form.no_documento) {
      return showModal("Atención", "Ingresa tu número de documento", "error");
    }

    if (!form.celular || form.celular.trim().length < 7) {
      return showModal("Celular requerido", "Por favor ingresa un número de celular válido para recibir el SMS de verificación.", "error");
    }

    if (form.password !== form.confirm_password) {
      return showModal("Atención", "Las contraseñas no coinciden", "error");
    }

    if (form.role === "ARRENDADOR") {
      if (!isDireccionValid && form.direccion) {
        return showModal("Dirección inválida", "La dirección de la persona no cumple con el formato estándar.", "error");
      }
      if (form.empresa_id === "NEW" && form.empresa_direccion && !isEmpresaAddressValid) {
        return showModal("Dirección inválida", "La dirección de la empresa no cumple con el formato estándar.", "error");
      }
    }

    if (!form.terminos_aceptados) {
      return showModal("Términos y condiciones", "Debes aceptar los términos y condiciones para continuar.", "error");
    }

    setLoading(true);
    try {
      // 1. Verificar si el documento ya existe
      const docCheckRes = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/auth/check-document/?no_documento=${form.no_documento}`);
      const docCheckData = await docCheckRes.json();
      if (docCheckData.exists) {
        setLoading(false);
        return showModal("Registro duplicado", "El número de documento ya está registrado y activo. Por favor inicia sesión.", "error");
      }

      // Normalizar número celular a formato estándar (+57)
      const rawPhone = String(form.celular || "").trim().replace(/[\s-]+/g, "");
      const digits = rawPhone.replace(/\D/g, "");
      let normalizedPhone = rawPhone;
      if (digits.length === 10 && digits.startsWith("3")) {
        normalizedPhone = `+57${digits}`;
      } else if (digits.length === 12 && digits.startsWith("57")) {
        normalizedPhone = `+${digits}`;
      }

      // 2. Crear o actualizar usuario y enviar códigos
      const payload = { 
        ...form,
        celular: normalizedPhone,
        device_fingerprint: getDeviceFingerprint()
      };
      delete payload.confirm_password;

      const response = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/auth/register/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.celular) {
          setForm(prev => ({ ...prev, celular: data.celular }));
        }
        if (data.email_verificado !== undefined || data.sms_verificado !== undefined) {
          setChannelStatus({
            email_verificado: !!data.email_verificado,
            sms_verificado: !!data.sms_verificado,
          });
        }
        showModal(
          docCheckData.pending_verification ? "Datos Actualizados" : "Paso 1 Completado",
          data.message || "Hemos enviado los códigos de verificación a tu correo y celular.",
          "success"
        );
        setStep(2);
      } else {
        showModal("Error al registrar", data.error || "No se pudo procesar el registro. Por favor revisa los datos.", "error");
      }
    } catch (err) {
      showModal("Error", "Error de conexión con el servidor.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Actualizar teléfono directamente en el Paso 2
  const handleUpdatePhone = async () => {
    if (!newPhone || newPhone.trim().length < 7) {
      return showModal("Celular requerido", "Por favor ingresa un número de celular válido.", "error");
    }
    setSavingPhone(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/auth/update-phone/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          no_documento: form.no_documento,
          celular: newPhone.trim(),
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setForm(prev => ({ ...prev, celular: data.celular }));
        setEditingPhone(false);
        showModal("Celular Actualizado", data.message || "Se ha actualizado el número y enviado un nuevo código SMS.", "success");
      } else {
        showModal("Error", data.error || "No se pudo actualizar el número de celular.", "error");
      }
    } catch (err) {
      showModal("Error", "Error de conexión al actualizar el celular.", "error");
    } finally {
      setSavingPhone(false);
    }
  };

  // PASO 2: Validar códigos (Email + SMS)
  const handleSubmitStep2 = async (e) => {
    e.preventDefault();

    if (!channelStatus.email_verificado && !verification.email_code) {
      return showModal("Código de Correo", "Por favor ingresa el código recibido en tu correo electrónico.", "error");
    }
    if (!channelStatus.sms_verificado && !verification.sms_otp) {
      return showModal("Código SMS", "Por favor ingresa el código SMS recibido en tu celular.", "error");
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/auth/verify-registration/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          no_documento: form.no_documento,
          email_code: verification.email_code,
          sms_otp: verification.sms_otp,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Almacenar sesión y redirigir
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", data.user);
        localStorage.setItem("full_name", data.name);
        localStorage.setItem("role", data.role);
        localStorage.setItem("role_label", data.role_label);
        localStorage.setItem("persona_exists", data.persona_exists);
        if (data.has_consented) {
          localStorage.setItem("has_consented_policies", "true");
        }
        if (data.org_display) localStorage.setItem("org_display", data.org_display);

        showModal("¡Verificación Exitosa!", "Tu cuenta ha sido verificada y activada con éxito. Ingresando al sistema...", "success");

        setTimeout(() => {
          if (data.role === "ARRENDADOR" && !data.persona_exists) {
            navigate("/personas/crear");
          } else if (data.role === "ARRENDATARIO") {
            navigate("/consultar-puntaje");
          } else {
            navigate("/referencias");
          }
        }, 1500);
      } else {
        if (data.email_verificado !== undefined || data.sms_verificado !== undefined) {
          setChannelStatus(prev => ({
            email_verificado: data.email_verificado !== undefined ? data.email_verificado : prev.email_verificado,
            sms_verificado: data.sms_verificado !== undefined ? data.sms_verificado : prev.sms_verificado,
          }));
        }
        showModal("Verificación Fallida", data.error || "Los códigos ingresados no son válidos. Por favor verifica e intenta de nuevo.", "error");
      }
    } catch (err) {
      showModal("Error", "Error de conexión al validar los códigos.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Reenviar códigos de verificación
  const handleResendCodes = async (tipo = "ambos") => {
    setResending(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/auth/resend-codes/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          no_documento: form.no_documento,
          tipo: tipo,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        if (data.email_verificado !== undefined || data.sms_verificado !== undefined) {
          setChannelStatus(prev => ({
            email_verificado: data.email_verificado !== undefined ? data.email_verificado : prev.email_verificado,
            sms_verificado: data.sms_verificado !== undefined ? data.sms_verificado : prev.sms_verificado,
          }));
        }
        if (data.completada) {
          showModal("Cuenta Verificada", data.message || "Tu cuenta ya está completamente verificada.", "success");
          setTimeout(() => navigate("/login"), 1500);
        } else {
          showModal("Códigos Reenviados", data.message || "Se han procesado los códigos de verificación.", "info");
        }
      } else {
        showModal("Error", data.error || "No se pudieron reenviar los códigos.", "error");
      }
    } catch (err) {
      showModal("Error", "Error de conexión al reenviar códigos.", "error");
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthShell
      eyebrow={isArrendatarioDomain ? "Portal de Arrendatarios" : `Creación de Usuario - Paso ${step} de 2`}
      title={step === 1 ? "Registro de Nuevo Usuario" : "Verificación de Cuenta (Email + SMS)"}
      subtitle={
        step === 1
          ? "Paso 1: Completa tus datos personales y de acceso para comenzar."
          : "Paso 2: Ingresa los códigos de seguridad enviados a tu correo y teléfono celular."
      }
      footerLinks={[
        { to: "/login", label: "Volver a iniciar sesión" },
        { to: "/forgot", label: "Recuperar contraseña" },
      ]}
    >
      {/* ======================================================== */}
      {/* PASO 1: INGRESO DE DATOS DEL USUARIO                    */}
      {/* ======================================================== */}
      {step === 1 && (
        <form onSubmit={handleSubmitStep1} autoComplete="off">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', padding: '10px 14px', background: '#eef6ff', borderRadius: '6px', borderLeft: '4px solid #0056b3' }}>
            <span style={{ fontSize: '18px' }}>📋</span>
            <span style={{ fontSize: '13px', color: '#1a365d' }}>
              <strong>Paso 1 de 2:</strong> Ingrese sus datos. En el siguiente paso verificará su correo electrónico y su número de celular mediante SMS.
            </span>
          </div>

          {!isArrendatarioDomain && (
            <div className="app-field">
              <label htmlFor="role">Tipo de Perfil</label>
              <select
                id="role"
                name="role"
                value={form.role}
                onChange={handleChange}
                className="app-select"
              >
                <option value="ARRENDADOR">Propietario / Inmobiliaria (Arrendador)</option>
              </select>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
            <div className="app-field">
              <label htmlFor="register-tipo">Tipo Documento</label>
              <select
                id="register-tipo"
                name="tipo_documento"
                value={form.tipo_documento}
                onChange={handleChange}
                className="app-select"
              >
                <option value="CC">Cédula (CC)</option>
                <option value="CE">Cédula Extranjería (CE)</option>
                <option value="NT">NIT</option>
                <option value="PA">Pasaporte</option>
                <option value="PR">Permiso Residencia</option>
              </select>
            </div>

            <div className="app-field">
              <label htmlFor="register-username">No. de Documento</label>
              <input
                id="register-username"
                type="text"
                name="no_documento"
                placeholder="Ej: 10203040"
                value={form.no_documento}
                onChange={handleChange}
                required
                className="app-input"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="app-field">
              <label htmlFor="first_name">Nombres</label>
              <input
                id="first_name"
                type="text"
                name="first_name"
                placeholder="Ej: Carlos Andrés"
                value={form.first_name}
                onChange={handleChange}
                required
                className="app-input"
              />
            </div>
            <div className="app-field">
              <label htmlFor="last_name">Apellidos</label>
              <input
                id="last_name"
                type="text"
                name="last_name"
                placeholder="Ej: Gómez Pérez"
                value={form.last_name}
                onChange={handleChange}
                required
                className="app-input"
              />
            </div>
          </div>

          <div className="app-field">
            <label htmlFor="email">Correo Electrónico (Verificación por Email)</label>
            <input
              id="email"
              type="email"
              name="email"
              placeholder="ejemplo@correo.com"
              value={form.email}
              onChange={handleChange}
              required
              className="app-input"
            />
            <small style={{ color: '#555', fontSize: '11px' }}>Recibirás un código de 6 dígitos en esta dirección.</small>
          </div>

          <div className="app-field">
            <label htmlFor="celular">Número de Celular (Verificación por SMS)</label>
            <input
              id="celular"
              type="tel"
              name="celular"
              placeholder="Ej: 3001234567"
              value={form.celular}
              onChange={handleChange}
              required
              className="app-input"
            />
            <small style={{ color: '#2b6cb0', fontSize: '11px', fontWeight: '500' }}>
              📲 Recibirás un mensaje de texto SMS con tu código OTP para completar el registro.
            </small>
          </div>

          {/* CAMPOS ESPECÍFICOS DE ARRENDADOR / INMOBILIARIA */}
          {form.role === "ARRENDADOR" && (
            <>
              <div className="app-field">
                <label htmlFor="register-tipo-org">Tipo de Arrendador / Organización</label>
                <select
                  id="register-tipo-org"
                  name="tipo_organizacion"
                  value={form.tipo_organizacion}
                  onChange={handleChange}
                  className="app-select"
                >
                  <option value="ARRENDADOR_INDEPENDIENTE">Arrendador Independiente / Propietario</option>
                  <option value="INMOBILIARIA">Inmobiliaria / Agencia</option>
                </select>
              </div>

              {form.tipo_organizacion === "INMOBILIARIA" && (
                <div className="app-field">
                  <label htmlFor="empresa_id">Inmobiliaria / Empresa</label>
                  <select
                    id="empresa_id"
                    name="empresa_id"
                    value={form.empresa_id}
                    onChange={handleChange}
                    required
                    className="app-select"
                  >
                    <option value="">Seleccione una empresa existente...</option>
                    <option value="NEW">+ Registrar nueva inmobiliaria</option>
                    {empresas.map(emp => (
                      <option key={emp.TEId} value={emp.TEId}>{emp.TENombre || emp.TEDescripcion}</option>
                    ))}
                  </select>
                </div>
              )}

              {form.empresa_id === "NEW" && (
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px', marginBottom: '12px', border: '1px solid #e2e8f0' }}>
                  <div className="app-field">
                    <label htmlFor="nombre_organizacion">Razón Social de la Inmobiliaria</label>
                    <input
                      id="nombre_organizacion"
                      type="text"
                      name="nombre_organizacion"
                      value={form.nombre_organizacion}
                      onChange={handleChange}
                      required
                      className="app-input"
                    />
                  </div>
                  <div className="app-field">
                    <label htmlFor="empresa_nit">NIT de la Inmobiliaria</label>
                    <input
                      id="empresa_nit"
                      type="text"
                      name="empresa_nit"
                      value={form.empresa_nit}
                      onChange={handleChange}
                      required
                      className="app-input"
                    />
                  </div>
                </div>
              )}

              <div className="app-field">
                <ColombianAddressInput
                  value={form.direccion}
                  onChange={handleChange}
                  onValidationChange={setIsDireccionValid}
                  name="direccion"
                  id="direccion"
                  label="Dirección de Residencia / Oficina"
                  placeholder="Ej: C 73B 45 89 APTO 7"
                  required={true}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="app-field">
                  <label htmlFor="barrio_zona">Barrio o Zona</label>
                  <input
                    id="barrio_zona"
                    type="text"
                    name="barrio_zona"
                    placeholder="Ej: Laureles"
                    value={form.barrio_zona}
                    onChange={handleChange}
                    required
                    className="app-input"
                  />
                </div>
                <div className="app-field">
                  <label htmlFor="ciudad_id">Ciudad</label>
                  <select
                    id="ciudad_id"
                    name="ciudad_id"
                    value={form.ciudad_id}
                    onChange={handleChange}
                    required
                    className="app-select"
                  >
                    <option value="">Seleccione ciudad...</option>
                    {ciudades.map(ciu => (
                      <option key={ciu.TCId} value={ciu.TCId}>{ciu.TCNombre} {ciu.TCDepartamento ? `- ${ciu.TCDepartamento}` : ''}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          {/* CONTRASEÑA */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="app-field">
              <label htmlFor="register-password">Contraseña</label>
              <input
                id="register-password"
                type="password"
                name="password"
                placeholder="Mínimo 6 caracteres"
                value={form.password}
                onChange={handleChange}
                required
                className="app-input"
              />
            </div>
            <div className="app-field">
              <label htmlFor="register-confirm-password">Confirmar Contraseña</label>
              <input
                id="register-confirm-password"
                type="password"
                name="confirm_password"
                placeholder="Repite la contraseña"
                value={form.confirm_password}
                onChange={handleChange}
                required
                className="app-input"
              />
            </div>
          </div>

          <TerminosYCondiciones />

          <div className="app-field" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', marginBottom: '20px' }}>
            <input
              id="terminos_aceptados"
              type="checkbox"
              name="terminos_aceptados"
              checked={form.terminos_aceptados}
              onChange={(e) => setForm({ ...form, terminos_aceptados: e.target.checked })}
              required
              style={{ width: 'auto' }}
            />
            <label htmlFor="terminos_aceptados" style={{ margin: 0, fontSize: '13px', cursor: 'pointer' }}>
              Acepto los términos y condiciones, política de privacidad y tratamiento de datos personales
            </label>
          </div>

          <button type="submit" disabled={loading} className="app-button app-button--primary" style={{ width: '100%', padding: '12px' }}>
            {loading ? "Creando usuario y enviando códigos..." : "Continuar a Verificación (Paso 1 de 2) →"}
          </button>
        </form>
      )}

      {/* ======================================================== */}
      {/* PASO 2: VERIFICACIÓN DUAL (EMAIL + SMS)                  */}
      {/* ======================================================== */}
      {step === 2 && (
        <form onSubmit={handleSubmitStep2} autoComplete="off">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', padding: '12px 14px', background: '#e6fffa', borderRadius: '6px', borderLeft: '4px solid #319795' }}>
            <span style={{ fontSize: '20px' }}>🔒</span>
            <div style={{ fontSize: '13px', color: '#234e52' }}>
              <strong>Paso 2 de 2: Doble Verificación de Seguridad</strong>
              <p style={{ margin: '4px 0 0 0' }}>Para proteger tu cuenta, requerimos verificar tu correo y tu celular antes de ingresar.</p>
            </div>
          </div>

          {/* VERIFICACIÓN 1: EMAIL */}
          {channelStatus.email_verificado ? (
            <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '1px solid #86efac', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#166534' }}>
                  ✓ 1. Correo Electrónico Verificado
                </span>
                <span style={{ fontSize: '12px', color: '#15803d' }}>{form.email}</span>
              </div>
              <p style={{ fontSize: '12px', color: '#166534', margin: '6px 0 0 0' }}>
                Tu correo electrónico ya ha sido confirmado exitosamente.
              </p>
            </div>
          ) : (
            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #cbd5e0', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#2d3748' }}>
                  ✉️ 1. Verificación por Correo Electrónico
                </span>
                <span style={{ fontSize: '12px', color: '#718096' }}>{form.email}</span>
              </div>
              <p style={{ fontSize: '12px', color: '#4a5568', margin: '0 0 10px 0' }}>
                Ingresa el código numérico de 6 dígitos que enviamos a tu buzón de correo electrónico:
              </p>
              <div className="app-field" style={{ marginBottom: '8px' }}>
                <input
                  id="email_code"
                  type="text"
                  name="email_code"
                  placeholder="Ej: 123456"
                  value={verification.email_code}
                  onChange={handleVerificationChange}
                  maxLength="36"
                  required={!channelStatus.email_verificado}
                  className="app-input"
                  style={{ fontSize: '18px', letterSpacing: '2px', textAlign: 'center', fontWeight: 'bold' }}
                />
              </div>
              <div style={{ textAlign: 'right' }}>
                <button
                  type="button"
                  onClick={() => handleResendCodes("email")}
                  disabled={resending}
                  style={{ background: 'none', border: 'none', color: '#3182ce', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  ¿No recibiste el correo? Reenviar código
                </button>
              </div>
            </div>
          )}

          {/* VERIFICACIÓN 2: SMS */}
          {channelStatus.sms_verificado ? (
            <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '1px solid #86efac', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#166534' }}>
                  ✓ 2. Celular Verificado por SMS
                </span>
                <span style={{ fontSize: '12px', color: '#15803d' }}>{form.celular}</span>
              </div>
              <p style={{ fontSize: '12px', color: '#166534', margin: '6px 0 0 0' }}>
                Tu número celular ya ha sido confirmado exitosamente por SMS.
              </p>
            </div>
          ) : (
            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #cbd5e0', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#2d3748' }}>
                  📲 2. Verificación por SMS al Celular
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#2b6cb0' }}>{form.celular}</span>
                  {!editingPhone && (
                    <button
                      type="button"
                      onClick={() => { setEditingPhone(true); setNewPhone(form.celular); }}
                      style={{ background: '#edf2f7', border: '1px solid #cbd5e0', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', color: '#2b6cb0', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      ✏️ Cambiar número
                    </button>
                  )}
                </div>
              </div>

              {editingPhone && (
                <div style={{ background: '#ebf8ff', padding: '12px', borderRadius: '6px', border: '1px solid #bee3f8', marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#2b6cb0', display: 'block', marginBottom: '4px' }}>
                    Modificar número de celular:
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      className="app-input"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      placeholder="Ej: +573208038075 o 3208038075"
                      style={{ flex: 1, fontSize: '14px' }}
                    />
                    <button
                      type="button"
                      className="app-button app-button--primary"
                      onClick={handleUpdatePhone}
                      disabled={savingPhone}
                      style={{ fontSize: '12px', padding: '6px 12px', whiteSpace: 'nowrap' }}
                    >
                      {savingPhone ? "Guardando..." : "Actualizar y Reenviar SMS"}
                    </button>
                    <button
                      type="button"
                      className="app-button app-button--secondary"
                      onClick={() => setEditingPhone(false)}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      Cancelar
                    </button>
                  </div>
                  <span style={{ fontSize: '11px', color: '#4a5568', marginTop: '4px', display: 'block' }}>
                    Se normalizará automáticamente con prefijo internacional +57.
                  </span>
                </div>
              )}

              <p style={{ fontSize: '12px', color: '#4a5568', margin: '0 0 10px 0' }}>
                Ingresa el código OTP de 6 dígitos recibido por SMS en tu celular (también enviado como respaldo a tu correo):
              </p>
              <div className="app-field" style={{ marginBottom: '8px' }}>
                <input
                  id="sms_otp"
                  type="text"
                  name="sms_otp"
                  placeholder="Ej: 654321"
                  value={verification.sms_otp}
                  onChange={handleVerificationChange}
                  maxLength="6"
                  required={!channelStatus.sms_verificado}
                  className="app-input"
                  style={{ fontSize: '18px', letterSpacing: '2px', textAlign: 'center', fontWeight: 'bold' }}
                />
              </div>
              <div style={{ textAlign: 'right' }}>
                <button
                  type="button"
                  onClick={() => handleResendCodes("sms")}
                  disabled={resending}
                  style={{ background: 'none', border: 'none', color: '#3182ce', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  ¿No recibiste el SMS? Reenviar código
                </button>
              </div>
            </div>
          )}

          <div className="app-actions" style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={() => setStep(1)}
              className="app-button app-button--secondary"
              style={{ flex: 1 }}
            >
              ← Modificar Datos
            </button>
            <button
              type="submit"
              disabled={loading}
              className="app-button app-button--primary"
              style={{ flex: 2, padding: '12px' }}
            >
              {loading ? "Verificando códigos..." : "Verificar y Activar Cuenta ✓"}
            </button>
          </div>
        </form>
      )}

      <AppModal 
        isOpen={modal.isOpen} 
        title={modal.title} 
        message={modal.message} 
        type={modal.type} 
        onClose={() => setModal({ ...modal, isOpen: false })} 
      />
    </AuthShell>
  );
}
